/**
 * Job journey — Customer → Lead → Quote → Invoice → Order
 * Pure helpers for linked-entity navigation strips.
 */

export const JOB_JOURNEY_KEYS = [
  "customer",
  "lead",
  "quote",
  "invoice",
  "order",
];

const LABEL_BY_KEY = {
  customer: "Customer",
  lead: "Lead",
  quote: "Quote",
  invoice: "Invoice",
  order: "Order",
};

/**
 * @param {...unknown} candidates
 * @returns {string|null}
 */
export function pickEntityId(...candidates) {
  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    const id = String(candidate).trim();
    if (id && id !== "undefined" && id !== "null") return id;
  }
  return null;
}

const PATH_BY_KEY = {
  customer: (id, ctx = {}) => {
    const params = new URLSearchParams();
    const leadId = pickEntityId(ctx.leadId);
    const quoteId = pickEntityId(ctx.quoteId);
    if (leadId) params.set("leadId", leadId);
    if (quoteId) params.set("quoteId", quoteId);
    const query = params.toString();
    return query ? `/customers/${id}?${query}` : `/customers/${id}`;
  },
  lead: (id) => `/leads/${id}`,
  quote: (id) => `/quotes/${id}`,
  invoice: (id) => `/invoices/${id}`,
  order: (id) => `/orders/${id}`,
};

/**
 * First non-empty human-readable label (never invents from empty values).
 * @param {...unknown} candidates
 * @returns {string|null}
 */
export function pickDisplayLabel(...candidates) {
  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    const label = String(candidate).trim();
    if (label && label !== "undefined" && label !== "null") return label;
  }
  return null;
}

/**
 * @param {Object|null|undefined} customer
 * @param {...unknown} fallbacks
 */
export function customerDisplayFrom(customer, ...fallbacks) {
  return pickDisplayLabel(
    customer?.name,
    customer?.customerName,
    ...fallbacks
  );
}

/**
 * @param {Object|null|undefined} lead
 */
export function leadDisplayFrom(lead) {
  return pickDisplayLabel(lead?.referenceNumber, lead?.name);
}

/**
 * @param {Object|null|undefined} quote
 */
export function quoteDisplayFrom(quote) {
  return pickDisplayLabel(quote?.referenceNumber, quote?.customerName);
}

/**
 * Invoice business ids (INV-…) are meaningful references.
 * @param {Object|null|undefined} invoice
 */
export function invoiceDisplayFrom(invoice) {
  return pickDisplayLabel(invoice?.id, invoice?._id);
}

/**
 * Order / job ids are the operator-facing reference.
 * @param {Object|null|undefined} order
 */
export function orderDisplayFrom(order) {
  return pickDisplayLabel(order?.id, order?.jobId, order?._id);
}

/**
 * Chip text: expanded current step shows detail; others stay compact.
 * @param {{
 *   label: string,
 *   detail?: string|null,
 *   linked: boolean,
 *   expanded?: boolean,
 * }} step
 */
export function formatJourneyChipLabel(step) {
  if (!step?.linked) return `${step.label} —`;
  if (step.expanded && step.detail) return `${step.label} · ${step.detail}`;
  return step.label;
}

/**
 * Hover / accessibility text always includes full detail when known.
 * @param {{
 *   label: string,
 *   detail?: string|null,
 *   linked: boolean,
 *   current?: boolean,
 * }} step
 */
export function formatJourneyChipTooltip(step) {
  const full = step?.detail
    ? `${step.label} · ${step.detail}`
    : step?.label || "";
  if (!step?.linked) return `${step.label} not linked yet`;
  if (step.current) return `Current: ${full}`;
  return `Open ${full}`;
}

/**
 * Normalize quote list payloads from /quotes.
 * @param {unknown} data
 * @returns {Array<Object>}
 */
export function normalizeQuoteList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.quotes)) return data.quotes;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

/**
 * Normalize invoice list payloads from /invoices.
 * @param {unknown} data
 * @returns {Array<Object>}
 */
export function normalizeInvoiceList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.invoices)) return data.invoices;
  return [];
}

/**
 * Prefer newest non-cancelled quote (draft/pending included).
 * @param {Array<Object>} quotes
 * @returns {Object|null}
 */
export function pickPrimaryQuote(quotes) {
  const list = Array.isArray(quotes) ? quotes : [];
  const active = list.filter(
    (q) => String(q?.status || "").toLowerCase() !== "cancelled"
  );
  const pool = active.length > 0 ? active : list;
  if (pool.length === 0) return null;

  const sorted = [...pool].sort((a, b) => {
    const at = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
    const bt = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
    return bt - at;
  });
  return sorted[0] || null;
}

/**
 * Prefer a non-cancelled invoice matching quoteId; fall back to first match.
 * @param {Array<Object>} invoices
 * @param {string|null} quoteId
 * @returns {string|null}
 */
export function pickPrimaryInvoiceId(invoices, quoteId = null) {
  const list = Array.isArray(invoices) ? invoices : [];
  const filtered = quoteId
    ? list.filter((inv) => {
        const invQuoteId = pickEntityId(inv?.quoteId);
        return invQuoteId && invQuoteId === String(quoteId).trim();
      })
    : list;

  if (filtered.length === 0) return null;

  const active = filtered.find(
    (inv) => String(inv?.status || "").toLowerCase() !== "cancelled"
  );
  const chosen = active || filtered[0];
  return pickEntityId(chosen?.id, chosen?._id);
}

/**
 * @param {Array<Object>} invoices
 * @param {string|null} quoteId
 * @returns {Object|null}
 */
export function pickPrimaryInvoice(invoices, quoteId = null) {
  const id = pickPrimaryInvoiceId(invoices, quoteId);
  if (!id) return null;
  const list = Array.isArray(invoices) ? invoices : [];
  return (
    list.find((inv) => pickEntityId(inv?.id, inv?._id) === id) || null
  );
}

/**
 * @param {{
 *   current: 'customer'|'lead'|'quote'|'invoice'|'order',
 *   customerId?: string|null,
 *   leadId?: string|null,
 *   quoteId?: string|null,
 *   invoiceId?: string|null,
 *   orderId?: string|null,
 *   customerDisplay?: string|null,
 *   leadDisplay?: string|null,
 *   quoteDisplay?: string|null,
 *   invoiceDisplay?: string|null,
 *   orderDisplay?: string|null,
 * }} input
 */
export function buildJobJourneySteps({
  current,
  customerId = null,
  leadId = null,
  quoteId = null,
  invoiceId = null,
  orderId = null,
  customerDisplay = null,
  leadDisplay = null,
  quoteDisplay = null,
  invoiceDisplay = null,
  orderDisplay = null,
} = {}) {
  const ids = {
    customer: pickEntityId(customerId),
    lead: pickEntityId(leadId),
    quote: pickEntityId(quoteId),
    invoice: pickEntityId(invoiceId),
    order: pickEntityId(orderId),
  };

  const details = {
    customer: pickDisplayLabel(customerDisplay),
    lead: pickDisplayLabel(leadDisplay),
    quote: pickDisplayLabel(quoteDisplay),
    invoice: pickDisplayLabel(invoiceDisplay),
    order: pickDisplayLabel(orderDisplay),
  };

  return JOB_JOURNEY_KEYS.map((key) => {
    const id = ids[key];
    const detail = details[key];
    const isCurrent = current === key;
    const linked = Boolean(id);
    const label = LABEL_BY_KEY[key];
    const pathContext = {
      leadId: ids.lead,
      quoteId: ids.quote,
    };
    return {
      key,
      label,
      id,
      detail,
      chipLabel: formatJourneyChipLabel({
        label,
        detail,
        linked,
        expanded: isCurrent,
      }),
      tooltip: formatJourneyChipTooltip({
        label,
        detail,
        linked,
        current: isCurrent,
      }),
      path: id ? PATH_BY_KEY[key](id, pathContext) : null,
      current: isCurrent,
      linked,
    };
  });
}

/**
 * Seed links from a lead record (may be incomplete until resolved).
 * Prefers formal conversion ids, then automated provisional quote id.
 * @param {Object|null|undefined} lead
 * @param {{
 *   quoteId?: string|null,
 *   quoteDisplay?: string|null,
 *   invoiceId?: string|null,
 *   invoiceDisplay?: string|null,
 *   orderId?: string|null,
 *   orderDisplay?: string|null,
 * }} [extras]
 */
export function seedFromLead(lead, extras = {}) {
  return {
    current: "lead",
    customerId: pickEntityId(lead?.customerId),
    leadId: pickEntityId(lead?.id, lead?._id),
    quoteId: pickEntityId(
      extras.quoteId,
      lead?.convertedQuoteId,
      lead?.tracking?.conversionData?.quoteId,
      lead?.automatedPricing?.quoteId
    ),
    invoiceId: pickEntityId(extras.invoiceId),
    orderId: pickEntityId(extras.orderId),
    customerDisplay: pickDisplayLabel(lead?.customerName),
    leadDisplay: leadDisplayFrom(lead),
    quoteDisplay: pickDisplayLabel(extras.quoteDisplay),
    invoiceDisplay: pickDisplayLabel(extras.invoiceDisplay),
    orderDisplay: pickDisplayLabel(extras.orderDisplay),
  };
}

/**
 * @param {Object|null|undefined} quote
 * @param {{
 *   invoiceId?: string|null,
 *   invoiceDisplay?: string|null,
 *   leadDisplay?: string|null,
 * }} [extras]
 */
export function seedFromQuote(quote, extras = {}) {
  return {
    current: "quote",
    customerId: pickEntityId(quote?.customerId),
    leadId: pickEntityId(quote?.leadId),
    quoteId: pickEntityId(quote?.id, quote?._id),
    invoiceId: pickEntityId(extras.invoiceId),
    orderId: pickEntityId(quote?.orderId, quote?.payment?.orderId),
    customerDisplay: pickDisplayLabel(quote?.customerName),
    leadDisplay: pickDisplayLabel(extras.leadDisplay),
    quoteDisplay: quoteDisplayFrom(quote),
    invoiceDisplay: pickDisplayLabel(extras.invoiceDisplay),
    orderDisplay: pickDisplayLabel(
      extras.orderDisplay,
      quote?.orderId,
      quote?.payment?.orderId
    ),
  };
}

/**
 * @param {Object|null|undefined} invoice
 */
export function seedFromInvoice(invoice) {
  return {
    current: "invoice",
    customerId: pickEntityId(invoice?.customerId),
    leadId: pickEntityId(invoice?.leadId),
    quoteId: pickEntityId(invoice?.quoteId),
    invoiceId: pickEntityId(invoice?.id, invoice?._id),
    orderId: pickEntityId(invoice?.jobId, invoice?.orderId),
    customerDisplay: pickDisplayLabel(invoice?.customerName),
    leadDisplay: null,
    quoteDisplay: pickDisplayLabel(invoice?.quoteReference),
    invoiceDisplay: invoiceDisplayFrom(invoice),
    orderDisplay: pickDisplayLabel(invoice?.jobId, invoice?.orderId),
  };
}

/**
 * @param {Object|null|undefined} order
 * @param {{
 *   customerId?: string|null,
 *   leadId?: string|null,
 *   quoteId?: string|null,
 *   invoiceId?: string|null,
 *   customerDisplay?: string|null,
 *   leadDisplay?: string|null,
 *   quoteDisplay?: string|null,
 *   invoiceDisplay?: string|null,
 * }} [extras]
 */
export function seedFromOrder(order, extras = {}) {
  return {
    current: "order",
    customerId: pickEntityId(extras.customerId, order?.customerId),
    leadId: pickEntityId(extras.leadId),
    quoteId: pickEntityId(extras.quoteId, order?.quoteId),
    invoiceId: pickEntityId(extras.invoiceId),
    orderId: pickEntityId(order?.id, order?._id, order?.jobId),
    customerDisplay: pickDisplayLabel(
      extras.customerDisplay,
      order?.customer,
      order?.customerName
    ),
    leadDisplay: pickDisplayLabel(extras.leadDisplay),
    quoteDisplay: pickDisplayLabel(extras.quoteDisplay),
    invoiceDisplay: pickDisplayLabel(extras.invoiceDisplay),
    orderDisplay: orderDisplayFrom(order),
  };
}
